import { buildZoneBoundsFeature } from "@sitescene/shared";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { TextArea } from "@/components/ui/textarea/textarea";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowLocation, ShadowZone } from "../../types/location-map";

import { useUpdateZone } from "../../hooks/useMapEntityUpdates";
import { createSensorMarkerFeature } from "../../map/markers/create-marker-feature";
import { getRefId } from "../../utils/location/installation-ids";
import { floorsForInstallation } from "../../utils/location/zone-floor";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";
import { toTitleCase } from "../../utils/shared/strings";
import { MapPlacementSection } from "./shared/map-placement-section";
import { ZoneFloorSelect } from "./zone-floor-select";

type EditZoneFormProps = {
	location: ShadowLocation;
	mapCenter?: [number, number] | null;
	onClose: () => void;
	zone: ShadowZone;
};

type EditZoneFormValues = {
	description: string;
	floorId: string;
	name: string;
};

/**
 * Dashboard-style Manage Zone modal for Shadow Realm list (and shared) edit.
 * Updates name/description; map is a read-only preview of zone sensors.
 */
export const EditZoneForm = ({
	location,
	mapCenter,
	onClose,
	zone,
}: EditZoneFormProps) => {
	const updateZone = useUpdateZone();
	const [submitError, setSubmitError] = useState<null | string>(null);

	const zoneFloors = useMemo(
		() =>
			floorsForInstallation(location.floors ?? [], getRefId(zone.installation)),
		[location.floors, zone.installation],
	);

	const {
		control,
		formState: { errors, isValid },
		handleSubmit,
		reset,
	} = useForm<EditZoneFormValues>({
		defaultValues: {
			description: zone.description ?? "",
			floorId: getRefId(zone.floor) ?? "",
			name: toTitleCase(zone.name),
		},
		mode: "onChange",
	});

	useEffect(() => {
		queueMicrotask(() => {
			reset({
				description: zone.description ?? "",
				floorId: getRefId(zone.floor) ?? "",
				name: toTitleCase(zone.name),
			});
		});
	}, [reset, zone.description, zone.floor, zone.id, zone.name]);

	const zoneSensors = useMemo(() => zone.sensors ?? [], [zone.sensors]);

	const zoneSensorMarkers = useMemo(
		() =>
			zoneSensors
				.filter((sensor) =>
					isValidCoordinatePair(sensor.geolocation?.coordinates),
				)
				.map((sensor) =>
					createSensorMarkerFeature({
						coordinates: sensor.geolocation!.coordinates as [number, number],
						id: sensor.id,
						lastInputReceived: sensor.lastInputReceived,
						locationId: location.id,
						name: sensor.name,
						status: sensor.status,
						type: sensor.type,
						zoneId: zone.id,
					}),
				),
		[location.id, zone.id, zoneSensors],
	);

	const otherZoneMarkers = useMemo(() => {
		const zoneSensorIds = new Set(zoneSensorMarkers.map((marker) => marker.id));
		return (location.zones ?? [])
			.filter((entry) => entry.id !== zone.id)
			.flatMap((entry) =>
				(entry.sensors ?? [])
					.filter(
						(sensor) =>
							!zoneSensorIds.has(sensor.id) &&
							isValidCoordinatePair(sensor.geolocation?.coordinates),
					)
					.map((sensor) =>
						createSensorMarkerFeature({
							coordinates: sensor.geolocation!.coordinates as [number, number],
							dimmed: true,
							id: sensor.id,
							lastInputReceived: sensor.lastInputReceived,
							locationId: location.id,
							name: sensor.name,
							status: sensor.status,
							type: sensor.type,
							zoneId: entry.id,
						}),
					),
			);
	}, [location.id, location.zones, zone.id, zoneSensorMarkers]);

	const mapMarkers = useMemo(() => {
		const markers = [...otherZoneMarkers, ...zoneSensorMarkers];
		return markers.length > 0 ? markers : undefined;
	}, [otherZoneMarkers, zoneSensorMarkers]);

	const zoneBoundsOverlay = useMemo(() => {
		const coordinates = zoneSensors
			.map((sensor) => sensor.geolocation?.coordinates)
			.filter(isValidCoordinatePair) as [number, number][];

		const polygon = buildZoneBoundsFeature({
			coordinates,
			storedBoundary: zone.boundary ?? null,
		});
		return polygon ? [polygon] : undefined;
	}, [zone.boundary, zoneSensors]);

	const hasSensors = zoneSensors.length > 0;
	const hasGeolocatedSensors = zoneSensorMarkers.length > 0;

	const mapFrameCenter = useMemo((): [number, number] | undefined => {
		if (zoneSensorMarkers[0]?.geometry.type === "Point") {
			const coords = zoneSensorMarkers[0].geometry.coordinates;
			if (isValidCoordinatePair(coords as [number, number])) {
				return coords as [number, number];
			}
		}
		if (isValidCoordinatePair(mapCenter ?? undefined)) {
			return mapCenter as [number, number];
		}
		return isValidCoordinatePair(location.coordinates?.coordinates)
			? location.coordinates!.coordinates
			: undefined;
	}, [location.coordinates, mapCenter, zoneSensorMarkers]);

	const canSubmit = isValid && !updateZone.isPending;

	const onSubmit = handleSubmit((data) => {
		const trimmedDescription = data.description.trim();
		setSubmitError(null);
		updateZone.mutate(
			{
				body: {
					description:
						trimmedDescription.length > 0 ? trimmedDescription : null,
					floor: data.floorId || null,
					name: data.name.trim(),
				},
				zoneId: zone.id,
			},
			{
				onError: () => {
					setSubmitError("Could not update zone.");
				},
				onSuccess: () => {
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description="Used in alerts and location maps."
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			panelClassName={hasGeolocatedSensors ? "sm:max-w-160" : undefined}
			submitDisabled={!canSubmit}
			submitText={updateZone.isPending ? "Saving…" : "Save"}
			title="Manage Zone"
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							errorMessage={errors.name?.message}
							isInvalid={Boolean(errors.name)}
							isRequired
							label="Zone Name"
							onBlur={field.onBlur}
							onChange={field.onChange}
							placeholder="Enter zone name"
							ref={field.ref}
							size="md"
							value={field.value}
						/>
					)}
					rules={{
						required: "Zone name is required",
						validate: (value) =>
							value.trim().length > 0 || "Zone name is required",
					}}
				/>

				<Controller
					control={control}
					name="description"
					render={({ field }) => (
						<TextArea
							errorMessage={errors.description?.message}
							isInvalid={Boolean(errors.description)}
							label="Description"
							onChange={field.onChange}
							placeholder="Optional description"
							rows={3}
							value={field.value}
						/>
					)}
				/>

				<Controller
					control={control}
					name="floorId"
					render={({ field }) => (
						<ZoneFloorSelect
							floors={zoneFloors}
							onChange={field.onChange}
							value={field.value}
						/>
					)}
				/>

				<hr className="my-1 border-secondary" />

				<div className="flex flex-col gap-2">
					<h3 className="text-sm font-semibold text-primary">Zone Map</h3>
					<p className="text-sm text-tertiary">
						{hasGeolocatedSensors
							? "Sensors in this zone with an approximate boundary derived from their map coordinates. Sensors from other zones appear dimmed for reference."
							: hasSensors
								? "Sensors in this zone do not have map coordinates yet."
								: "No sensors have been added to this zone."}
					</p>
				</div>

				{hasGeolocatedSensors ? (
					<MapPlacementSection
						center={mapFrameCenter}
						fitBoundsKey={zone.id}
						hasPartialCoordinates={false}
						markers={mapMarkers}
						overlays={zoneBoundsOverlay}
					/>
				) : null}

				{submitError ? (
					<p className="text-sm text-error-primary">{submitError}</p>
				) : null}
			</Form>
		</GeneralModal>
	);
};
